import { forwardRef } from "react";

import { AccessibilityProps } from "../../AccessibilityProps";
import { withSubComponents } from "../../utilities/withSubComponents";
import { InputBaseProps } from "../InputBaseProps";
import { RefComponent } from "../RefComponent";
import { Field } from "./Field";
import { FileDeleteEventHandler } from "./FileDeleteEventHandler";
import { FileEntry } from "./FileEntry";
import { Input } from "./Input";
import { Placeholder } from "./Placeholder";

export interface FileUploadBaseProps {
  /**
   * The set of allowed file extensions.
   */
  readonly allowedExtensions?: ReadonlyArray<string>;

  /**
   * The maximum file size in bytes.
   */
  readonly maxSize?: number;

  /**
   * Indicates whether multiple files are able to be selected for upload. If not set defaults to `false`.
   */
  readonly allowMultiple?: boolean;

  /**
   * The function to call when a file should be deleted.
   */
  readonly onDelete?: FileDeleteEventHandler;

  /**
   * Accessibility label for the file upload component.
   */
  readonly "aria-label"?: AccessibilityProps["aria-label"];

  /**
   * Accessibility label for the file upload component.
   */
  readonly "aria-labelledby"?: AccessibilityProps["aria-labelledby"];
  /**
   * Accessibility description for the file upload component.
   */
  readonly "aria-describedby"?: AccessibilityProps["aria-describedby"];
}

/**
 * The `FileUpload` component properties.
 */
export interface FileUploadProps
  extends Omit<InputBaseProps<ReadonlyArray<FileEntry> | null>, "instructions" | "hideInstructions">,
    FileUploadBaseProps {}

/**
 * The `FileUpload` component sub components.
 */
export interface FileUploadSubComponents {
  /**
   * A React Hook Form compatible FileUpload component. For more information, see the documentation for
   * the <a href="https://react-hook-form.com/api">React Hook Form API</a>.
   */
  readonly Field: typeof Field;

  /**
   * A placeholder component for the `FileUpload` which can be displayed while loading.
   */
  readonly Placeholder: typeof Placeholder;
}

export const FileUpload = withSubComponents<RefComponent<HTMLDivElement, FileUploadProps>, FileUploadSubComponents>(
  {
    Field,
    Placeholder,
  },
  forwardRef<HTMLDivElement, FileUploadProps>(function FileUpload(props, ref) {
    return <Input {...props} ref={ref} />;
  }),
);
